import React from 'react';
import { Link } from './Link';
import type { AlgoliaCompanyRecord } from '../types/algolia-company-record';
import type { CompanyProfileUiCopy } from '../utils/company/company-profile-ui-copy';

interface SubScoreRowProps {
  label: string;
  score: number; // 0–1
  weight: string;
}

function SubScoreRow({ label, score, weight }: SubScoreRowProps) {
  const pct = Math.round(score * 100);
  const barColour =
    pct >= 70
      ? 'bg-[#3B5E45] dark:bg-[#5A8A68]'
      : pct >= 45
        ? 'bg-[#8A7350] dark:bg-[#C4A882]'
        : 'bg-red-500 dark:bg-red-600';
  return (
    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
      <span className="w-[4.25rem] sm:w-28 shrink-0 text-xs text-[#5A4A3A] dark:text-[#B8A48C] truncate" title={label}>
        {label}
      </span>
      <div className="flex-1 min-w-0 bg-[#F2EADF] dark:bg-[#2A1D0F] rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${barColour}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-7 sm:w-8 shrink-0 text-right text-xs font-medium text-[#1E1A14] dark:text-[#F2EADF] tabular-nums">
        {pct}
      </span>
      <span className="w-9 sm:w-10 shrink-0 text-right text-xs text-[#7A6E5F] dark:text-[#B8A48C]">{weight}</span>
    </div>
  );
}

interface EmployerScoreCardProps {
  company: Pick<
    AlgoliaCompanyRecord,
    | 'companyName'
    | 'employerScore'
    | 'payScore'
    | 'flexibilityScore'
    | 'stabilityScore'
    | 'growthScore'
  >;
  copy: Pick<CompanyProfileUiCopy, 'employerScorePendingTitle' | 'employerScorePendingDescription'>;
  className?: string;
}

function scoreBadgeColour(score: number): string {
  if (score >= 70) return 'bg-[#3B5E45] text-white dark:bg-[#1A2E22] dark:text-[#98CFA8]';
  if (score >= 50) return 'bg-[#6E4F00] text-white dark:bg-[#2A1E08] dark:text-[#D4A820]';
  return 'bg-red-500 text-white dark:bg-red-900 dark:text-red-100';
}

export function EmployerScoreCard({ company, copy, className = '' }: EmployerScoreCardProps) {
  if (company.employerScore === undefined) {
    return (
      <div
        className={`rounded-2xl border border-[#DDD5C8] bg-white shadow-sm dark:border-[#4A3520] dark:bg-[#1F1509] p-4 sm:p-5 ${className}`}
      >
        <h2 className="text-base font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
          JobCrawls Employer Score
        </h2>
        <p className="mt-2 inline-flex items-center rounded-full bg-[#F2EADF] px-2.5 py-1 text-xs font-medium text-[#5A4A3A] dark:bg-[#2A1D0F] dark:text-[#C4A882]">
          {copy.employerScorePendingTitle}
        </p>
        <p className="mt-2 text-xs leading-relaxed text-[#7A6E5F] dark:text-[#B8A48C]">
          {copy.employerScorePendingDescription}
        </p>
      </div>
    );
  }

  const score = company.employerScore;

  /** Shares match `computeEmployerScore` composite (four wired pillars only, renormalized). */
  const subScores = [
    { label: 'Pay', score: company.payScore ?? 0, weight: '31%' },
    { label: 'Flexibility', score: company.flexibilityScore ?? 0, weight: '25%' },
    { label: 'Stability', score: company.stabilityScore ?? 0, weight: '25%' },
    { label: 'Growth', score: company.growthScore ?? 0, weight: '19%' },
  ];

  return (
    <div
      className={`rounded-2xl border border-[#DDD5C8] bg-white shadow-sm dark:border-[#4A3520] dark:bg-[#1F1509] p-4 sm:p-5 ${className}`}
    >
      <div className="mb-4 flex flex-col gap-3 min-[420px]:flex-row min-[420px]:items-start min-[420px]:justify-between">
        <div className="min-w-0 flex-1 pr-0 min-[420px]:pr-3">
          <h2 className="text-base font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
            JobCrawls Employer Score
          </h2>
          <p className="mt-0.5 text-xs text-[#5A4A3A] dark:text-[#B8A48C]">
            Computed from job data signals — not reviews
          </p>
        </div>
        <div className="flex shrink-0 justify-end min-[420px]:justify-start">
          <div
            className={`flex h-14 w-14 items-center justify-center rounded-xl text-xl font-bold tabular-nums ${scoreBadgeColour(score)}`}
            aria-label={`Employer score: ${score} out of 100`}
          >
            {score}
          </div>
        </div>
      </div>

      <div className="space-y-2 sm:space-y-2.5">
        {subScores.map(row => (
          <SubScoreRow key={row.label} {...row} />
        ))}
      </div>

      <p className="mt-4 text-xs leading-relaxed text-[#7A6E5F] dark:text-[#B8A48C]">
        Score is based on salary competitiveness from posted bands (where we have enough listings),
        remote and hybrid flexibility, hiring stability over time, and growth signals from role mix
        and hiring volume trends. Benefits and ad-level transparency are omitted from the headline
        number until those feeds are wired into scoring.{' '}
        <Link
          href="/about-employer-score"
          className="font-medium text-[#3B5E45] underline decoration-[#3B5E45]/30 underline-offset-2 hover:text-[#2A4533] dark:text-[#98CFA8] dark:decoration-[#98CFA8]/40 dark:hover:text-[#B6D4BC]"
        >
          How we calculate this
        </Link>
      </p>
    </div>
  );
}

EmployerScoreCard.displayName = 'EmployerScoreCard';
